"use client";

import React from "react";
import { motion } from "framer-motion";

import Image from "next/image";

import { GroqImage } from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

import bgGrid2 from "@/public/frankfurt-event/bgGrid2.svg";

export default function Overview({
  data,
}: {
  data: PageEventSchema | PageKryptoDaySchema;
}) {
  return (
    <section className="relative pb-[4rem] overflow-clip lg:pb-[10rem]">
      <div className="px-[2rem] w-[min(152.5rem,100%)] mx-auto flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center"
        >
          <div className="font-bold bg-white bg-opacity-10 rounded-full border border-white border-opacity-20 px-[2rem] py-[.75rem] leading-[1em] text-[1.75rem] backdrop-blur-[2rem] mb-[2rem]">
            <GroqImage
              className="!w-[26rem]"
              src={data?.requirementsLogo}
              alt="logos"
            />
          </div>
          {data?.requirementsTexts && (
            <PortableText
              value={data?.requirementsTexts ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="text-[4rem] lg:text-[7rem] leading-[1.1em] font-semibold gradient-title !text-white/80 mb-[2rem] w-full text-center">
                      {children}
                    </h2>
                  ),
                  normal: ({ children }) => (
                    <p className="text-white text-opacity-60 text-[2rem] mb-[2rem] lg:mb-[4rem] w-[min(72.25rem,100%)] text-center">
                      {children}
                    </p>
                  ),
                },
              })}
            />
          )}
        </motion.div>

        <div className="grid grid-cols-1 w-full sm:grid-cols-2 lg:grid-cols-4 gap-[2rem]">
          {data?.requirementsCards &&
            data?.requirementsCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="flex relative flex-col items-center text-center bg-white bg-opacity-5 border border-white border-opacity-10 rounded-[2rem] pb-[3rem] py-[5rem] px-[4rem]"
              >
                <Image
                  className="!w-full absolute left-0 bottom-0 pointer-events-none"
                  src={bgGrid2}
                  alt=""
                />
                <GroqImage
                  className="!w-[7rem] drop-shadow-[0_0_56px_rgba(87,245,196,.20)] mb-[1rem]"
                  src={card?.icon}
                  alt="icon"
                />
                <p className="font-semibold text-white text-[3rem] mb-[1rem] leading-[1.1em]">
                  {card?.title}
                </p>
                <p className="text-white text-opacity-60">{card?.text}</p>
              </motion.div>
            ))}
        </div>

        {data?.overviewCTA && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="[&_*]:font-sans [&_*]:uppercase"
          >
            <CTAButton
              data={data?.overviewCTA}
              className="[&_.cta]:!py-[2.5rem] mt-[5rem]"
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
